<template>
  <div>
    <navigation-back class="lg:mb-5" label="Back" :route="isUserOnboarded ? 'user-dashboard' : 'onboarding-users'" />
    <div class="container mx-auto">
      <div class="mx-4 max-w-6xl">
        <doe-page-heading
          class="pb-6"
          label="Add educator"
          :description="[
            `You are adding an early childhood teacher or educator to ${serviceName}.`,
            'At the end of this step, a Department of Education (DoE) account will be created for your teacher or educator, and they will receive DoE account login details to access the Self-Assessment and Quality Improvement Planning Portal.'
          ]"
        />
        <div class="mb-4" v-if="!isSchoolPrincipal">
          <doe-fieldset-heading label="Does your educator already have a DoE account?" />
        </div>
        <div class="flex flex-wrap mb-6" v-if="!isSchoolPrincipal">
          <div
            class="w-full md:w-1/4"
            v-for="(option, index) in options"
            :key="index"
          >
            <label class="wrapper" :for="option.text">
              <input
                type="radio"
                :id="option.text"
                name="services"
                :value="option.value"
                v-model="hasAccount"
              />
              <span class="checkmark"></span>
              {{ option.text }}
            </label>
          </div>
        </div>

        <form-generator
          v-if="hasAccount === 'no'"
          v-model="educatorUser"
          :schema="schemaFull"
          @form="updateState(educatorUser)"
          @form:next="formNext()"
          @form:previous="formPrevious()"
        />
        <form-generator
          v-if="hasAccount === 'yes' || isSchoolPrincipal"
          v-model="educatorUser"
          :schema="schemaUsername"
          @form="updateState(educatorUser)"
          @form:next="formNext()"
          @form:previous="formPrevious()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { serviceCallWithAuth } from "@/helpers/cognito";
import handleRoles from "@/mixins/handleRoles";
import schemaFull from "@/schemas/user/userCreateFull";
import schemaUsername from "@/schemas/user/userCreateUsername";
import NavigationBack from "@/components/navigation/NavigationBack";
import FormGenerator from "@/components/form/FormGenerator";
import DoePageHeading from "@/components/typography/PageHeading";
import DoeFieldsetHeading from "@/components/typography/FieldsetHeading";

export default {
  name: "CreateUser",
  mixins: [handleRoles],
  inject: {
    $validator: "$validator"
  },
  components: {
    NavigationBack,
    FormGenerator,
    DoePageHeading,
    DoeFieldsetHeading
  },
  data() {
    return {
      schemaFull: schemaFull,
      schemaUsername: schemaUsername,
      hasAccount: "",
      options: [{ text: "Yes", value: "yes" }, { text: "No", value: "no" }]
    };
  },
  computed: {
    ...mapGetters(["getService", "getEducatorUser"]),
    profile() {
      return this.$OAuth.data.profile;
    },
    educatorUser() {
      return this.getEducatorUser;
    },
    isUserOnboarded() {
      return (this.aclArray.length > 1)
    },
    serviceData() {
      return this.getService;
    },
    serviceName() {
      return this.serviceData.serviceTradingAsName;
    }
  },
  methods: {
    ...mapActions(["SAVE_EDUCATOR_FIELD", "CLEAR_EDUCATOR"]),
    updateState(data) {
      this.SAVE_EDUCATOR_FIELD(data);
    },
    formPrevious() {
      this.$router.push({ name: "user-dashboard" });
    },
    formNext() {
      const seNumber = this.getService.seNumber;
      const detUserGuid = this.profile.detuserguid;
      const serviceRole = this.getServiceRole;
      this.educatorUser.seNumber = seNumber;
      this.educatorUser.role = "Educator";

      // if educator to be added doesnt have existing DOE account
      if(this.hasAccount==="no" && this.profile.email === this.educatorUser.userEmail){
        this.$root.translateModal(`As Director at ${seNumber}, ${this.profile.detuserid} educator access is not required.`, "Error");
        return;
      }
      // if educator to be added has existing DOE account
      if(this.hasAccount==="yes" && this.profile.detuserid.toLowerCase() === this.educatorUser.userUsername.trim().toLowerCase()){
        this.$root.translateModal(`As Director at ${seNumber}, ${this.profile.detuserid} educator access is not required.`, "Error");
        return;
      }

      let headers = this.$OAuth.buildHttpHeader();
      Object.keys(this.educatorUser).forEach(key=> {
        if(typeof this.educatorUser[key]==="string"){
          this.educatorUser[key] = this.educatorUser[key].trim();
        }
      })
      let options = {
        path: "/users/new",
        method: "POST",
        headers: headers,
        body: JSON.stringify(this.educatorUser)
      };
      serviceCallWithAuth("doe-ttsds", options)
        .then(response => {
          this.CLEAR_EDUCATOR();
          if (response.statusCode !== 200) {
            this.$root.translateModal(
              "The username was not found. Please try again.",
              "Error"
            );
          } else {
            this.$root.translateModal(
              `The educator has been ${
                this.hasAccount === "no" ? "created" : "located"
              }. They will be added to your service shortly.`,
              "Success"
            );
          }
          return this.$router.push({
            name: this.isUserOnboarded ? "onboarding-complete" : "user-dashboard"
          });
        })
        .catch(reason => {
          if (reason.message) {
            this.$root.translateModal(reason.message);
          } else {
            this.$root.translateModal(reason, "Error");
          }
        })
        .finally(() => {
          this.$root.isLoading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.wrapper input[type="radio"] {
  @apply absolute overflow-hidden h-0 w-0;
  clip: rect(1px, 1px, 1px, 1px);
  position: absolute;
  opacity: 0;
  // cursor: pointer;
  height: 0;
  width: 0;
}
.wrapper {
  @apply cursor-pointer relative font-sans mb-4 pl-6 text-base text-gray-900 leading-loose inline-block subpixel-antialiased w-full;
  display: block;
  position: relative;
  padding-left: 35px;
  // margin-bottom: 12px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  top: -3px;
}
input[type="radio"] + label:before {
  @apply absolute bg-transparent block border-2 border-blue-900 rounded;
  content: " ";
  top: 0px;
  left: 0px;
  width: 1.5rem;
  height: 1.5rem;
}

/* Create a custom checkbox */
.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 1.5rem;
  width: 1.5rem;
  background-color: transparent;
  border-radius: 0.25rem;
  border-width: 2px;
  border-color: #041e42;
  border-style: solid;
}

/* On mouse-over, add a grey background color */
.checkmark:hover {
  background-color: #ccc;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.wrapper input[type="radio"]:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.wrapper .checkmark:after {
  left: 6px;
  top: 1px;
  width: 8px;
  height: 16px;
  border: solid #041e42;
  border-width: 0 4px 4px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}

input[type="radio"]:focus + span.checkmark {
  @apply border-blue-900;
  box-shadow: 0 0 0 1px #041e42;
  outline: 1px solid #041e42;
  outline-offset: 3px;
}
</style>
